import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { computeClaims, type FieldClaim, type PeerPresence } from './claims';

/**
 * The slice of a realtime broadcast channel the session hook needs. The
 * production implementation wraps a Supabase Realtime channel
 * (see supabaseChannel.ts); tests substitute an in-memory double.
 */
export interface PresenceChannel {
  /**
   * Register the handler for incoming presence messages. Called before
   * subscribe. Payloads arrive untrusted off the wire; the session decodes
   * them into PresenceMessage and drops anything malformed.
   */
  onMessage(cb: (payload: Record<string, unknown>) => void): void;
  subscribe(onStatus: (status: string) => void): void;
  /** Broadcast a presence message to the topic's other members. */
  send(payload: PresenceMessage): Promise<unknown>;
  unsubscribe(): Promise<unknown>;
}

/**
 * The wire protocol: every message names its sending connection and user.
 * `state` announces that connection's current claim (field_key null = none),
 * `hello` asks holders to re-broadcast into a fresh joiner's empty map,
 * `bye` withdraws a connection immediately on clean teardown (ADR 0002).
 */
export type PresenceMessage =
  | {
      kind: 'state';
      conn_id: string;
      user_id: string;
      field_key: string | null;
      claimed_at: number | null;
      announced_at: number;
    }
  | { kind: 'hello'; conn_id: string; user_id: string }
  | { kind: 'bye'; conn_id: string; user_id: string };

/** Decode an untrusted wire payload, null for anything malformed. */
function decodePresenceMessage(payload: Record<string, unknown>): PresenceMessage | null {
  const { kind, conn_id, user_id } = payload;
  if (typeof conn_id !== 'string' || typeof user_id !== 'string') return null;
  switch (kind) {
    case 'hello':
    case 'bye':
      return { kind, conn_id, user_id };
    case 'state':
      return {
        kind,
        conn_id,
        user_id,
        field_key: typeof payload.field_key === 'string' ? payload.field_key : null,
        claimed_at: typeof payload.claimed_at === 'number' ? payload.claimed_at : null,
        announced_at: typeof payload.announced_at === 'number' ? payload.announced_at : 0,
      };
    default:
      return null;
  }
}

export interface PresenceSession {
  /** Field key → the displayed claims, earliest first (never the local user's own). */
  claims: Record<string, FieldClaim[]>;
  /** Publish which field the local user has focused (null = none). */
  setFocusedField(fieldKey: string | null): void;
}

/**
 * Everything rides broadcast on the private template channel (ADR 0002); the
 * message kinds are documented on PresenceMessage. Liveness is client-side:
 * a held claim re-broadcasts every HEARTBEAT_MS, and peers drop a connection
 * unseen for LIVENESS_TTL_MS — measured by local receipt time, never by
 * comparing the sender's clock against ours.
 */
const HEARTBEAT_MS = 15_000;
const LIVENESS_TTL_MS = 2 * HEARTBEAT_MS;
const SWEEP_MS = 5_000;
/** Spread hello replies so N holders don't answer a joiner in one burst. */
const HELLO_REPLY_MAX_JITTER_MS = 1_000;

/** Backoff for reopening a channel the server closed. */
const REJOIN_DELAY_MS = 1_000;
const REJOIN_MAX_DELAY_MS = 30_000;

/**
 * One remembered connection id per topic, per tab. On web it survives a page
 * reload (sessionStorage is per-tab): the reloaded tab rejoins as the same
 * connection, so its fresh announce *replaces* the ghost claim peers still
 * hold for it instead of waiting out the liveness TTL. Native never reloads;
 * the module map only keeps remount/rejoin ids stable there.
 */
const rememberedConnIds = new Map<string, string>();

function connIdForTopic(topic: string): string {
  const remembered = rememberedConnIds.get(topic);
  if (remembered) return remembered;
  const storageKey = `rbf.presence.conn:${topic}`;
  let id: string | null = null;
  if (typeof sessionStorage !== 'undefined') {
    try {
      id = sessionStorage.getItem(storageKey);
    } catch {
      // Storage access can throw (privacy modes); fall back to a fresh id.
    }
  }
  id ??= Math.random().toString(36).slice(2);
  rememberedConnIds.set(topic, id);
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.setItem(storageKey, id);
    } catch {
      // Best effort — without storage a reload ghost just expires by TTL.
    }
  }
  return id;
}

/** A peer connection's last-announced state, stamped with local receipt time. */
interface ConnState {
  userId: string;
  fieldKey: string | null;
  claimedAt: number | null;
  announcedAt: number;
  lastSeenAt: number;
}

/**
 * A live presence session on one topic: announces this connection's focus
 * claims and exposes everyone else's. Spectators (`canClaim: false`)
 * subscribe and hello without ever announcing. See CONTEXT.md: Focus claim.
 */
export function usePresenceSession(opts: {
  topic: string;
  userId: string | null;
  canClaim: boolean;
  createChannel(topic: string): PresenceChannel;
}): PresenceSession {
  const { topic, userId, canClaim } = opts;
  const [peers, setPeers] = useState<PeerPresence[]>([]);

  const channelRef = useRef<PresenceChannel | null>(null);
  const subscribedRef = useRef(false);
  /** True once this connection has announced state since its last (re)join. */
  const announcedRef = useRef(false);
  const fieldKeyRef = useRef<string | null>(null);
  const claimedAtRef = useRef<number | null>(null);
  const connIdRef = useRef<string>('');
  const connsRef = useRef(new Map<string, ConnState>());
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const flushPendingRef = useRef(false);
  // Kept in refs so the channel effect doesn't tear down on every render (the
  // factory is typically an inline closure) and setFocusedField stays stable.
  // Mirrored in an every-render effect (writing refs during render is illegal);
  // it registers before the channel effect, so the channel effect never reads
  // stale values on the commit that (re)creates the channel.
  const createChannelRef = useRef(opts.createChannel);
  const userIdRef = useRef(userId);
  const canClaimRef = useRef(canClaim);
  useEffect(() => {
    createChannelRef.current = opts.createChannel;
    userIdRef.current = userId;
    canClaimRef.current = canClaim;
  });

  const publishPeers = useCallback(() => {
    setPeers(
      [...connsRef.current.values()].map((c) => ({
        userId: c.userId,
        fieldKey: c.fieldKey,
        claimedAt: c.claimedAt,
      })),
    );
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const sendState = useCallback(
    function sendStateNow() {
      const channel = channelRef.current;
      if (!channel || !userIdRef.current) return;
      if (!subscribedRef.current || !canClaimRef.current) return;
      announcedRef.current = true;
      void channel.send({
        kind: 'state',
        conn_id: connIdRef.current,
        user_id: userIdRef.current,
        field_key: fieldKeyRef.current,
        claimed_at: claimedAtRef.current,
        announced_at: Date.now(),
      });
      // A held claim heartbeats so peers' liveness TTL never expires it; an
      // idle connection carries no information and stays silent.
      stopHeartbeat();
      if (fieldKeyRef.current != null) {
        heartbeatTimerRef.current = setInterval(sendStateNow, HEARTBEAT_MS);
      }
    },
    [stopHeartbeat],
  );

  const sendBye = useCallback(() => {
    const channel = channelRef.current;
    if (!channel || !userIdRef.current || !announcedRef.current) return;
    announcedRef.current = false;
    stopHeartbeat();
    void channel.send({
      kind: 'bye',
      conn_id: connIdRef.current,
      user_id: userIdRef.current,
    });
  }, [stopHeartbeat]);

  useEffect(() => {
    if (!userId) return;
    let disposed = false;
    let rejoinTimer: ReturnType<typeof setTimeout> | null = null;
    let rejoinDelay = REJOIN_DELAY_MS;
    let sweepTimer: ReturnType<typeof setInterval> | null = null;
    let helloReplyTimer: ReturnType<typeof setTimeout> | null = null;

    const stopSessionTimers = () => {
      if (sweepTimer) {
        clearInterval(sweepTimer);
        sweepTimer = null;
      }
      if (helloReplyTimer) {
        clearTimeout(helloReplyTimer);
        helloReplyTimer = null;
      }
      stopHeartbeat();
    };

    const sweep = () => {
      const cutoff = Date.now() - LIVENESS_TTL_MS;
      let changed = false;
      for (const [connId, conn] of connsRef.current) {
        if (conn.lastSeenAt < cutoff) {
          connsRef.current.delete(connId);
          changed = true;
        }
      }
      if (changed) publishPeers();
    };

    const open = () => {
      const channel = createChannelRef.current(topic);
      channelRef.current = channel;
      connIdRef.current = connIdForTopic(topic);
      announcedRef.current = false;

      channel.onMessage((payload) => {
        const msg = decodePresenceMessage(payload);
        if (!msg) return;
        // A duplicated tab copies sessionStorage and then shares our conn id
        // for its whole life; its messages must not enter our peer map (nor
        // ours its). Peers may see the one conn id flap between the two tabs'
        // claims — accepted: same user, and claims are advisory.
        if (msg.conn_id === connIdRef.current) return;
        switch (msg.kind) {
          case 'bye':
            if (connsRef.current.delete(msg.conn_id)) publishPeers();
            return;
          case 'hello':
            // Fill the joiner's empty map: claim holders re-broadcast,
            // jittered so a room of holders doesn't answer in one burst. Idle
            // connections stay silent — no claim, nothing to fill.
            if (
              fieldKeyRef.current != null &&
              canClaimRef.current &&
              subscribedRef.current &&
              !helloReplyTimer
            ) {
              helloReplyTimer = setTimeout(() => {
                helloReplyTimer = null;
                sendState();
              }, Math.random() * HELLO_REPLY_MAX_JITTER_MS);
            }
            return;
          case 'state': {
            const existing = connsRef.current.get(msg.conn_id);
            // Latest announcement per connection wins; announced_at values
            // only ever compare against the same sender's clock, so skew is
            // harmless.
            if (existing && msg.announced_at < existing.announcedAt) return;
            connsRef.current.set(msg.conn_id, {
              userId: msg.user_id,
              fieldKey: msg.field_key,
              claimedAt: msg.claimed_at,
              announcedAt: msg.announced_at,
              lastSeenAt: Date.now(),
            });
            publishPeers();
          }
        }
      });

      channel.subscribe((status) => {
        // Any non-SUBSCRIBED status means peers can no longer be observed
        // live; stale claims must not keep gating fields — offline degrades to
        // plain last-write-wins editing (claims are advisory, see ADR 0001).
        if (status !== 'SUBSCRIBED') {
          subscribedRef.current = false;
          announcedRef.current = false;
          connsRef.current.clear();
          setPeers([]);
          stopSessionTimers();
          // A clean close is how the server evicts a channel (e.g. a rate
          // limit), and realtime-js never rejoins one on its own — reopen it
          // ourselves. Errors and timeouts are left to realtime-js's internal
          // rejoin logic.
          if (status === 'CLOSED' && !disposed && !rejoinTimer) {
            rejoinTimer = setTimeout(() => {
              rejoinTimer = null;
              if (disposed) return;
              void channel.unsubscribe();
              open();
            }, rejoinDelay);
            rejoinDelay = Math.min(rejoinDelay * 2, REJOIN_MAX_DELAY_MS);
          }
          return;
        }
        subscribedRef.current = true;
        rejoinDelay = REJOIN_DELAY_MS;
        if (!sweepTimer) sweepTimer = setInterval(sweep, SWEEP_MS);
        if (!userIdRef.current) return;
        // Ask holders to fill our empty map (any member may send; spectators
        // included — the broadcast insert policy spans the whole membership).
        void channel.send({
          kind: 'hello',
          conn_id: connIdRef.current,
          user_id: userIdRef.current,
        });
        // The join announce replaces any ghost of our own conn id (reload,
        // rejoin) and re-announces a field still held across a rejoin.
        if (canClaimRef.current) sendState();
      });
    };
    open();

    return () => {
      disposed = true;
      if (rejoinTimer) clearTimeout(rejoinTimer);
      stopSessionTimers();
      flushPendingRef.current = false;
      // Clean departures clear instantly for peers; the liveness TTL is only
      // for connections that never got to say goodbye (ADR 0002).
      sendBye();
      const channel = channelRef.current;
      channelRef.current = null;
      subscribedRef.current = false;
      if (channel) void channel.unsubscribe();
    };
    // canClaim is deliberately NOT a dependency: it flips false→true on every
    // editor mount (the viewer's role arrives from a synced query), and
    // rejoining the topic for it races the previous channel's async removal —
    // supabase-js would hand back the dying instance and its late teardown
    // killed the live channel. The effect below handles claim-mode changes on
    // the existing channel instead.
  }, [topic, userId, publishPeers, sendState, sendBye, stopHeartbeat]);

  useEffect(() => {
    const channel = channelRef.current;
    if (!channel) return;
    if (canClaim) {
      // Not subscribed yet (or announced already)? The subscribe handler picks
      // this up via canClaimRef when the join completes.
      if (!subscribedRef.current || announcedRef.current || !userIdRef.current) return;
      sendState();
    } else {
      // Claiming revoked: withdraw this connection entirely so peers stop
      // seeing a claim we may no longer hold.
      sendBye();
    }
  }, [canClaim, topic, sendState, sendBye]);

  const setFocusedField = useCallback(
    (fieldKey: string | null) => {
      // No-op suppression: a redundant focus event must not re-announce (or
      // refresh the claim timestamp of) the field already held.
      if (fieldKey === fieldKeyRef.current) return;
      fieldKeyRef.current = fieldKey;
      claimedAtRef.current = fieldKey ? Date.now() : null;
      if (!canClaimRef.current || !subscribedRef.current) return;
      // Coalesce per microtask: a blur→focus hop lands as one message
      // carrying the final state — zero added latency, and peers never see
      // the transient release between two fields.
      if (flushPendingRef.current) return;
      flushPendingRef.current = true;
      void Promise.resolve().then(() => {
        if (!flushPendingRef.current) return;
        flushPendingRef.current = false;
        sendState();
      });
    },
    [sendState],
  );

  const claims = useMemo(
    () => (userId ? computeClaims(peers, userId) : {}),
    [peers, userId],
  );

  return { claims, setFocusedField };
}
